import { useEffect, useRef, useState, FormEvent } from "react";
import { contactsApi, Contact } from "../api/contacts";
import { useAuth } from "../hooks/useAuth";
import { sha256Hex } from "../lib/crypto";

interface ContactForm {
  roleName: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  portalEnabled: boolean;
}

const EMPTY_FORM: ContactForm = {
  roleName: "",
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  portalEnabled: false,
};

const INPUT_BASE =
  "w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500";

export default function ContactsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [noCompany, setNoCompany] = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; kind: "success" | "error" } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showToast(msg: string, kind: "success" | "error" = "success") {
    setToast({ msg, kind });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }

  // ── Add modal ─────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<ContactForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // ── Edit modal ────────────────────────────────────────────────────────────
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editForm, setEditForm] = useState<ContactForm>(EMPTY_FORM);
  const [editSubmitting, setEditSubmitting] = useState(false);

  // ── Delete confirmation ───────────────────────────────────────────────────
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  function loadContacts() {
    contactsApi
      .list()
      .then((data) => {
        setContacts(data);
        setLoading(false);
      })
      .catch((err) => {
        if (err?.message?.includes("No company")) setNoCompany(true);
        setLoading(false);
      });
  }

  useEffect(loadContacts, []);

  // ── Add contact ────────────────────────────────────────────────────────────
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setAddError(null);

    if (form.password && form.password !== form.confirmPassword) {
      setAddError("Passwords do not match");
      return;
    }
    if (form.portalEnabled && !form.password) {
      setAddError("A password is required when enabling portal access");
      return;
    }

    setSubmitting(true);
    try {
      const passwordHash = form.password ? await sha256Hex(form.password) : undefined;
      await contactsApi.create({
        roleName: form.roleName || undefined,
        name: form.name,
        email: form.email || undefined,
        passwordHash,
        portalEnabled: form.portalEnabled || undefined,
      });
      setShowModal(false);
      loadContacts();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Failed to add contact");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Edit contact ───────────────────────────────────────────────────────────
  function openEditModal(c: Contact) {
    setEditForm({
      roleName: c.role_name ?? "",
      name: c.name,
      email: c.email ?? "",
      password: "",
      confirmPassword: "",
      portalEnabled: c.portal_enabled === 1,
    });
    setEditingContact(c);
  }

  async function handleEditSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editingContact) return;

    if (editForm.password && editForm.password !== editForm.confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }

    setEditSubmitting(true);
    try {
      const passwordHash = editForm.password ? await sha256Hex(editForm.password) : undefined;
      await contactsApi.update(editingContact.id, {
        roleName: editForm.roleName || undefined,
        name: editForm.name,
        email: editForm.email || undefined,
        passwordHash,
        portalEnabled: editForm.portalEnabled,
      });
      setContacts((cs) =>
        cs.map((c) =>
          c.id === editingContact.id
            ? {
                ...c,
                role_name: editForm.roleName || null,
                name: editForm.name,
                email: editForm.email || null,
                portal_enabled: editForm.portalEnabled ? 1 : 0,
              }
            : c
        )
      );
      setEditingContact(null);
      showToast("Contact updated");
    } catch {
      showToast("Failed to update contact", "error");
    } finally {
      setEditSubmitting(false);
    }
  }

  // ── Delete contact ─────────────────────────────────────────────────────────
  async function handleDelete() {
    if (!deletingContact) return;
    setDeleteSubmitting(true);
    try {
      await contactsApi.remove(deletingContact.id);
      setContacts((cs) => cs.filter((c) => c.id !== deletingContact.id));
      setDeletingContact(null);
      showToast("Contact deleted");
    } catch {
      showToast("Failed to delete contact", "error");
      setDeletingContact(null);
    } finally {
      setDeleteSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Contacts</h2>
          <p className="text-sm text-gray-500 mt-0.5">Shore-side contact directory</p>
        </div>
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Loading…</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[60] rounded-lg px-5 py-3 text-sm font-medium text-white shadow-lg ${
          toast.kind === "error" ? "bg-red-600" : "bg-green-600"
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Page header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Contacts</h2>
          <p className="text-sm text-gray-500 mt-0.5">Shore-side contact directory</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setForm(EMPTY_FORM); setAddError(null); setShowModal(true); }}
            className="rounded-lg bg-navy-700 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 transition-colors"
          >
            Add Contact
          </button>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6">
        {noCompany && (
          <div className="mb-4 rounded-lg bg-yellow-50 border border-yellow-200 px-5 py-4 text-sm text-yellow-800">
            Your account is not assigned to a company. Contact an administrator to set this up.
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Portal</th>
                {isAdmin && <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contacts.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3 text-gray-500">{c.role_name ?? "—"}</td>
                  <td className="px-5 py-3 font-medium text-gray-900">{c.name}</td>
                  <td className="px-5 py-3 text-gray-600">{c.email ?? "—"}</td>
                  <td className="px-5 py-3">
                    {c.portal_enabled === 1 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        Disabled
                      </span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-4">
                        <button
                          onClick={() => openEditModal(c)}
                          className="text-xs font-medium text-navy-700 hover:text-navy-900 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeletingContact(c)}
                          className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} className="px-5 py-10 text-center text-sm text-gray-400">
                    No contacts yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Delete confirmation modal ─────────────────────────────────────── */}
      {deletingContact && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setDeletingContact(null); }}
        >
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete contact?</h2>
            <p className="text-sm text-gray-600 mb-6">
              Delete <span className="font-medium text-gray-900">{deletingContact.name}</span>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeletingContact(null)}
                className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteSubmitting}
                className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteSubmitting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Contact modal ────────────────────────────────────────────── */}
      {editingContact && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setEditingContact(null); }}
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-900">Edit Contact</h2>
              <button
                onClick={() => setEditingContact(null)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="Full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <input
                  value={editForm.roleName}
                  onChange={(e) => setEditForm((f) => ({ ...f, roleName: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="e.g. Technical Dept."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="contact@example.com"
                />
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Department Portal Access</p>

                <label className="flex items-center gap-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={editForm.portalEnabled}
                    onChange={(e) => setEditForm((f) => ({ ...f, portalEnabled: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-navy-700"
                  />
                  <span className="text-sm text-gray-700">Enable portal access for this contact</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                    <span className="ml-1 text-xs font-normal text-gray-400">(leave blank to keep existing)</span>
                  </label>
                  <input
                    type="password"
                    value={editForm.password}
                    onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                    className={`${INPUT_BASE} border-gray-300`}
                    placeholder="Min. 8 characters"
                    minLength={editForm.password ? 8 : undefined}
                  />
                </div>

                {editForm.password && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={editForm.confirmPassword}
                      onChange={(e) => setEditForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                      className={`${INPUT_BASE} border-gray-300`}
                      placeholder="Repeat password"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setEditingContact(null)}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSubmitting || !editForm.name}
                  className="flex-1 rounded-lg bg-navy-700 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {editSubmitting ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add Contact modal ─────────────────────────────────────────────── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-900">Add Contact</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {addError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {addError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="Full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <input
                  value={form.roleName}
                  onChange={(e) => setForm((f) => ({ ...f, roleName: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="e.g. Technical Dept."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="contact@example.com"
                />
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Department Portal Access</p>

                <label className="flex items-center gap-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={form.portalEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, portalEnabled: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-navy-700"
                  />
                  <span className="text-sm text-gray-700">Enable portal access for this contact</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Portal Password
                    {form.portalEnabled && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    className={`${INPUT_BASE} border-gray-300`}
                    placeholder="Min. 8 characters"
                    minLength={form.password ? 8 : undefined}
                  />
                </div>

                {form.password && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={form.confirmPassword}
                      onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                      className={`${INPUT_BASE} border-gray-300`}
                      placeholder="Repeat password"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !form.name}
                  className="flex-1 rounded-lg bg-navy-700 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {submitting ? "Adding…" : "Add Contact"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
